/**
 * The share-codes screen's controller: `shareCodes.model.ts` wired to
 * `fetchShareCodes` for the ledger, a SUPPLIED reader for the work picker, and
 * `issueShareCode` / `endShareCode` for the writes.
 *
 * It owns what the model cannot: the issue draft, the code just issued (shown
 * once), the two-press confirmation on ending one, the busy markers, and the
 * reload nonce that re-reads both lists after every write. The model decides
 * which codes are live and what each row says.
 *
 * The picker is read only to fill the select, and a failure on that read is an
 * empty picker rather than an error — the codes are the subject.
 *
 * THE PICKER'S SOURCE IS A PARAMETER, and that is the whole of what the owner's
 * arm needed. `GET /v1/share-codes` already answers for an owner — the owner
 * branch of `ondo.my_share_codes()` — but `my_agent_mandates()` is EMPTY for
 * one, so hard-calling it here would have given the owner the ledger with a
 * picker that could never offer anything. The two readers below are the two
 * suppliers; nothing else on this screen differs but the words.
 */

import { useEffect, useState } from "react";
import type { ShareCode } from "@ondo/shared";

import {
  endShareCode,
  fetchAgentMandates,
  fetchShareCodes,
  issueShareCode,
  searchPublishableWorks,
} from "../lib/api.ts";
import { PICKER_LIMIT, SHARE_CODES_COPY } from "./shareCodes.constants.ts";
import {
  describeShareCodesError,
  issuableWorks,
  ownedIssuableWorks,
  toShareCodesView,
} from "./shareCodes.model.ts";
import type { IssuableWork, ShareCodesView } from "./shareCodes.model.ts";
import type { ShareCodesCopy } from "./shareCodes.constants.ts";

/** Where the work picker's rows come from. See the note above. */
export type IssuableWorksReader = () => Promise<IssuableWork[]>;

/** The AGENT's picker: what other people have put in their hands. */
export const readAgentIssuableWorks: IssuableWorksReader = () =>
  fetchAgentMandates().then(issuableWorks);

/**
 * The OWNER's picker: the works they own, from the same relation
 * `issue_share_code` accepts.
 *
 * ONE PAGE, because the picker is a `<select>` and a select cannot search.
 * `search_owned_works` orders by title with an empty query, so an owner of more
 * than `PICKER_LIMIT` works would see the first hundred by title and not the
 * rest. That is a real limit rather than a hidden one: the day somebody holds
 * more, this wants the searched, paged `WorkPickerDialog` the viewing rooms
 * use, not a bigger number here — the API clamps `limit` at 100 anyway.
 */
export const readOwnedIssuableWorks: IssuableWorksReader = () =>
  searchPublishableWorks("", PICKER_LIMIT, 0).then((page) => ownedIssuableWorks(page.works));

export interface IssueDraft {
  workId: string;
  days: number;
  intendedFor: string;
  note: string;
}

export interface ShareCodesController {
  view: ShareCodesView;
  draft: IssueDraft;
  setDraft: (patch: Partial<IssueDraft>) => void;
  issue: () => void;
  issuing: boolean;
  /** The code just issued, shown once beside the form. */
  justIssued: string | null;
  /** Ending, in two presses. */
  askToEnd: (shareCodeId: string) => void;
  cancelEnd: () => void;
  end: (shareCodeId: string) => void;
  confirmingId: string | null;
  working: string | null;
  error: string | null;
}

const EMPTY_DRAFT: IssueDraft = { workId: "", days: 14, intendedFor: "", note: "" };

/** What the screen is, from whichever side the reader holds it. */
export interface ShareCodesInput {
  /** '/agent/ONDO-1017/work' or '/collector/me/work' — where a row links to. */
  workBase: string;
  /** The firm to issue for on a firm's workspace, or null. */
  asOndoId: string | null;
  /** Mandates for an agent, owned works for an owner. See the note at the top. */
  readIssuableWorks: IssuableWorksReader;
  copy?: ShareCodesCopy;
}

/**
 * Share codes, from the issuer's side.
 *
 * TWO READS. The codes are the subject; the supplied reader populates the work
 * picker, because a code is issued FOR a work and the issuer has to say which.
 * The picker is readable the moment those rows land, and a failure on that read
 * leaves the form saying there is nothing to issue for — which is true from
 * where the reader stands.
 *
 * RE-READ AFTER EVERY WRITE, the rule the money screens keep: issuing adds a
 * row and ending one changes who can read a work, and the only copy of either
 * fact is the table.
 */
export function useShareCodesController({
  workBase,
  asOndoId,
  readIssuableWorks,
  copy = SHARE_CODES_COPY,
}: ShareCodesInput): ShareCodesController {
  const [codes, setCodes] = useState<ShareCode[] | null>(null);
  const [works, setWorks] = useState<IssuableWork[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraftState] = useState<IssueDraft>(EMPTY_DRAFT);
  const [issuing, setIssuing] = useState(false);
  const [justIssued, setJustIssued] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;

    fetchShareCodes()
      .then((found) => {
        if (active) setCodes(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeShareCodesError(cause, copy));
      });

    void readIssuableWorks()
      .then((found) => {
        if (active) setWorks(found);
      })
      .catch(() => {
        if (active) setWorks([]);
      });

    return () => {
      active = false;
    };
    // BOTH SUPPLIED VALUES MUST BE STABLE REFERENCES, and both are: each call
    // site passes a module constant. They are named here rather than suppressed
    // because a stale closure over the wrong picker is silent, while an inline
    // arrow at a call site re-reading on every render is not — so if one of
    // them is ever built inside a component, this is where it announces itself.
  }, [nonce, readIssuableWorks, copy]);

  return {
    view: toShareCodesView({ codes, works, error, workBase, copy }),
    draft,
    setDraft: (patch) => setDraftState((current) => ({ ...current, ...patch })),
    issuing,
    justIssued,
    confirmingId,
    working,
    error,

    issue: () => {
      if (!draft.workId) return;
      setIssuing(true);
      setError(null);
      setJustIssued(null);

      issueShareCode(draft.workId, {
        days: draft.days,
        intendedFor: draft.intendedFor.trim() || null,
        note: draft.note.trim() || null,
        asOndoId,
      })
        .then(({ code }) => {
          setJustIssued(code);
          setDraftState(EMPTY_DRAFT);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeShareCodesError(cause, copy)))
        .finally(() => setIssuing(false));
    },

    askToEnd: (shareCodeId) => {
      setError(null);
      setConfirmingId(shareCodeId);
    },

    cancelEnd: () => setConfirmingId(null),

    end: (shareCodeId) => {
      setWorking(shareCodeId);
      setError(null);

      endShareCode(shareCodeId)
        .then(() => {
          setConfirmingId(null);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeShareCodesError(cause, copy)))
        .finally(() => setWorking(null));
    },
  };
}
